import { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { analyticsService } from '../services/analyticsService';

interface UsageData {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  requestCount: number;
  date?: string;
}

/**
 * Hook to track AI usage and budget
 * Fetches usage data on mount and provides refresh function
 */
export function useUsageTracking() {
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const lastThresholdReachedRef = useRef<number>(0);

  const fetchUsage = async () => {
    try {
      const response = await apiService.get('/editor/ai/usage');
      if (response.data.success) {
        const newUsage = response.data.usage;
        setUsageData(newUsage);
        
        // Track threshold events (50%, 75%, 90%, 100%)
        const thresholds = [50, 75, 90, 100];
        const newPercentage = newUsage.percentage;
        
        for (const threshold of thresholds) {
          if (newPercentage >= threshold && lastThresholdReachedRef.current < threshold) {
            analyticsService.track({
              name: threshold === 100 ? 'daily_limit_reached' : 'usage_threshold_reached',
              properties: {
                threshold,
                used: newUsage.used,
                limit: newUsage.limit,
                remaining: newUsage.remaining,
                percentage: newUsage.percentage,
                date: newUsage.date || new Date().toISOString(),
                context: 'usage_check'
              }
            });
            lastThresholdReachedRef.current = threshold;
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  // Fetch usage on mount
  useEffect(() => {
    fetchUsage();
  }, []);

  return { usageData, fetchUsage, setUsageData };
}

export type { UsageData };

